import { useQueryData } from "@src/hooks/useQueryData";
import { byCode, reloadAndReplaceCachedListItem } from "@src/hooks/queryListCache";
import {
    stockGatheringsServices,
    type StockGathering,
    type StockGatheringContentExtends,
    type StockGatheringExtends,
    type StockGatheringReserveItem,
    type StockReserveExtends,
} from "../../../../api/stockGatherings";

/** Include карточки КВ. */
export const STOCK_GATHERING_COMPLETION_CARD_INCLUDES = {
    include: ["SenderDivision", "SenderSectionData", "ReceiverDivision", "ReceiverSectionData", "State"] satisfies Array<
        keyof StockGatheringExtends
    >,
} as const;

/** Include строк состава КВ для списка резервов. */
export const STOCK_GATHERING_COMPLETION_CONTENT_INCLUDES = {
    contentInclude: [
        "StockObj",
        "Measure",
        "SenderDivisionData",
        "SenderSectionData",
        "ReceiverDivisionData",
        "ReceiverSectionData",
    ] satisfies Array<keyof StockGatheringContentExtends>,
} as const;

/** Include резервов КВ. */
export const STOCK_GATHERING_COMPLETION_RESERVE_INCLUDES = {
    reserveInclude: ["Lot", "Measure", "Order", "StorePlaceRemainders", "SpMovings"] satisfies Array<
        keyof StockReserveExtends
    >,
} as const;

/** Загружает шапку КВ для карточки комплектования. */
export function useStockGatheringCompletionCardQuery(gatheringCode: number) {
    return useQueryData<StockGathering | null>({
        queryKey: ["stock", "gatheringCompletion", gatheringCode, "card"],
        queryFn: ({ signal }) =>
            stockGatheringsServices.loadByCode(
                {
                    code: gatheringCode,
                    ...STOCK_GATHERING_COMPLETION_CARD_INCLUDES,
                },
                { signal },
            ),
        enabled: gatheringCode > 0,
        meta: { errorSource: "stock.gatheringCompletion.card" },
    });
}

/** Параметры точечной перезагрузки резерва КВ. */
export interface RefetchGatheringReserveParams {
    /** Код резерва. */
    reserveCode: number;
    /** Код строки состава КВ. */
    gatheringContCode: number;
}

/** Результат загрузки резервов КВ. */
export interface UseStockGatheringCompletionReservesQueryResult {
    /** Резервы строк состава КВ. */
    reserves: StockGatheringReserveItem[];
    /** Признак фоновой загрузки. */
    isFetching: boolean;
    /** Перезагружает резервы КВ. */
    refetch: () => Promise<unknown>;
    /**
     * Перезагружает один резерв и заменяет его в query cache. \
     * Если резерв исчез (полностью отпущен) — удаляет его из списка.
     * @param params код резерва и строки состава
     */
    refetchReserveByCode: (params: RefetchGatheringReserveParams) => Promise<void>;
}

/** Загружает состав КВ и резервы отдельным от шапки карточки запросом. */
export function useStockGatheringCompletionReservesQuery(
    gatheringCode: number,
): UseStockGatheringCompletionReservesQueryResult {
    const query = useQueryData<StockGatheringReserveItem[]>({
        queryKey: ["stock", "gatheringCompletion", gatheringCode, "reserves"],
        queryFn: ({ signal }) =>
            stockGatheringsServices.reserves.loadForContents(
                {
                    gatheringCode,
                    ...STOCK_GATHERING_COMPLETION_CONTENT_INCLUDES,
                    ...STOCK_GATHERING_COMPLETION_RESERVE_INCLUDES,
                },
                { signal },
            ),
        enabled: gatheringCode > 0,
        meta: { errorSource: "stock.gatheringCompletion.reserves" },
    });

    const refetchReserveByCode = async (params: RefetchGatheringReserveParams): Promise<void> => {
        await reloadAndReplaceCachedListItem({
            setData: query.setData,
            getKey: byCode,
            removeKeyIfMissing: params.reserveCode,
            loadItem: () =>
                stockGatheringsServices.reserves.loadOneForContent({
                    reserveCode: params.reserveCode,
                    gatheringContCode: params.gatheringContCode,
                    ...STOCK_GATHERING_COMPLETION_CONTENT_INCLUDES,
                    ...STOCK_GATHERING_COMPLETION_RESERVE_INCLUDES,
                }),
        });
    };

    return {
        reserves: query.data ?? [],
        isFetching: query.isFetching,
        refetch: query.refetch,
        refetchReserveByCode,
    };
}
